import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FC,
  type FocusEvent,
  type MouseEvent,
  type ReactNode,
  type TransitionEvent,
} from "react";
import { twMerge } from "tailwind-merge";

const HEIGHT_TRANSITION_DURATION_MS = 250;
const COLLAPSE_MEASUREMENT_FALLBACK_BUFFER_MS = 50;

export interface ExpandablePreviewProps {
  children: ReactNode;
  isExpanded: boolean;
  /** Keeps the body expanded and hides the toggle (e.g. while an editor inside it is open). */
  isExpansionForced?: boolean;
  onExpandedChange: (isExpanded: boolean) => void;
  /** Height of the collapsed content box, in pixels. */
  collapsedHeight: number;
  /** Height of the fade over the bottom of the collapsed content, in pixels. */
  fadeHeight: number;
  expandLabel?: string;
  collapseLabel?: string;
  className?: string;
}

/**
 * Clips its children under a fade with an expand toggle, and expands on its
 * own when focus or find-in-page reaches clipped content.
 */
export const ExpandablePreview: FC<ExpandablePreviewProps> = (props) => {
  const {
    children,
    isExpanded,
    isExpansionForced = false,
    onExpandedChange,
    collapsedHeight,
    fadeHeight,
    expandLabel = "Show more",
    collapseLabel = "Show less",
    className,
  } = props;
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const gradientRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasRequestedAutoExpansionRef = useRef(false);
  const isCollapsingRef = useRef(false);
  const collapseMeasurementTimerRef = useRef<number | undefined>(undefined);
  const [isOverflowing, setIsOverflowing] = useState(true);

  const bodyId = useId();
  const showsExpandedBody = isExpanded || isExpansionForced;
  const showsExpandedBodyRef = useRef(showsExpandedBody);

  const measureOverflow = useCallback(() => {
    const wrapperElement = wrapperRef.current;
    const bodyElement = bodyRef.current;

    if (!wrapperElement || !bodyElement) {
      return;
    }

    const wrapperStyles = getComputedStyle(wrapperElement);
    const collapsedContentHeight =
      wrapperElement.clientHeight -
      Number.parseFloat(wrapperStyles.paddingTop) -
      Number.parseFloat(wrapperStyles.paddingBottom);

    setIsOverflowing(bodyElement.scrollHeight > collapsedContentHeight);
  }, []);

  const finishCollapseMeasurement = useCallback(() => {
    window.clearTimeout(collapseMeasurementTimerRef.current);
    isCollapsingRef.current = false;
    if (!showsExpandedBodyRef.current) {
      measureOverflow();
    }
  }, [measureOverflow]);

  useLayoutEffect(() => {
    const wasShowingExpandedBody = showsExpandedBodyRef.current;
    showsExpandedBodyRef.current = showsExpandedBody;

    if (showsExpandedBody) {
      window.clearTimeout(collapseMeasurementTimerRef.current);
      isCollapsingRef.current = false;
      return;
    }

    if (!wasShowingExpandedBody) {
      return;
    }

    // Every expanded -> collapsed transition can animate, including a parent
    // releasing `isExpansionForced`. Guard overflow measurement before the
    // ResizeObserver starts watching that transition; measuring its interim
    // heights can briefly hide and remount the disclosure on every frame.
    isCollapsingRef.current = true;
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = 0;
    }
    collapseMeasurementTimerRef.current = window.setTimeout(
      finishCollapseMeasurement,
      HEIGHT_TRANSITION_DURATION_MS + COLLAPSE_MEASUREMENT_FALLBACK_BUFFER_MS,
    );
  }, [finishCollapseMeasurement, showsExpandedBody]);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    const bodyElement = bodyRef.current;

    if (!wrapperElement || !bodyElement || showsExpandedBody) {
      return;
    }

    const measureStableCollapsedOverflow = () => {
      if (!isCollapsingRef.current) {
        measureOverflow();
      }
    };

    // Measure against the real collapsed content box. The preview is flex-auto
    // so a grid-stretched card can make it taller than its collapsed height; a fixed sentinel
    // would then report overflow even when every line is already visible.
    measureStableCollapsedOverflow();

    const observer = new ResizeObserver(measureStableCollapsedOverflow);
    observer.observe(wrapperElement);
    observer.observe(bodyElement);

    return () => {
      observer.disconnect();
    };
  }, [measureOverflow, showsExpandedBody]);

  useEffect(
    () => () => {
      window.clearTimeout(collapseMeasurementTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!showsExpandedBody) {
      hasRequestedAutoExpansionRef.current = false;
    }
  }, [showsExpandedBody]);

  const requestAutoExpansion = () => {
    if (
      showsExpandedBody ||
      !isOverflowing ||
      hasRequestedAutoExpansionRef.current
    ) {
      return;
    }

    hasRequestedAutoExpansionRef.current = true;
    // Keyboard focus and find-in-page can scroll an overflow-hidden ancestor
    // before their event reaches us. Clear that browser-created offset before
    // changing height so the first expansion frame starts at the preview's top.
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = 0;
    }
    onExpandedChange(true);
  };

  const handleBodyFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    const wrapperElement = wrapperRef.current;
    const gradientElement = gradientRef.current;
    const focusTarget = event.target;

    if (
      showsExpandedBody ||
      !isOverflowing ||
      !wrapperElement ||
      !gradientElement ||
      !(focusTarget instanceof HTMLElement)
    ) {
      return;
    }

    const wrapperBounds = wrapperElement.getBoundingClientRect();
    const focusTargetBounds = focusTarget.getBoundingClientRect();
    // Focusing an overflow-hidden descendant can scroll the wrapper before the
    // focus event arrives. Add scrollTop back to recover the target's position
    // in the unscrolled content, then compare it with the start of the fade.
    const focusTargetBottom =
      focusTargetBounds.bottom - wrapperBounds.top + wrapperElement.scrollTop;
    // The preview reserves a paint gutter around its collapsed content box so
    // focus rings remain inside the clipping edge. Compare against the
    // gradient's real position rather than deriving it from clientHeight,
    // which includes that gutter.
    const unobscuredContentBottom = gradientElement.offsetTop;

    if (focusTargetBottom > unobscuredContentBottom) {
      requestAutoExpansion();
    }
  };

  const handleWrapperScroll = () => {
    // overflow-hidden has no user scroll affordance. A non-zero offset while
    // collapsed therefore comes from browser behavior such as focusing a
    // clipped control or revealing a find-in-page match; reveal the full body.
    if ((wrapperRef.current?.scrollTop ?? 0) > 0) {
      requestAutoExpansion();
    }
  };

  const handleWrapperClick = (event: MouseEvent<HTMLDivElement>) => {
    const clickTarget = event.target;
    if (
      showsExpandedBody ||
      !isOverflowing ||
      !(clickTarget instanceof Element) ||
      clickTarget.closest(
        "a, button, input, select, textarea, [role='button'], [role='link']",
      )
    ) {
      return;
    }

    requestAutoExpansion();
  };

  const handleWrapperTransitionEnd = (
    event: TransitionEvent<HTMLDivElement>,
  ) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === "height"
    ) {
      finishCollapseMeasurement();
    }
  };

  const handleToggleClick = () => {
    if (showsExpandedBody) {
      // Safari can leave focus on a previously activated descendant when a
      // pointer click does not focus the disclosure button. Move focus to the
      // persistent control before clipping that descendant again.
      if (bodyRef.current?.contains(document.activeElement)) {
        toggleButtonRef.current?.focus();
      }
      // A browser-driven focus/find scroll can survive until the intrinsic-size
      // transition finishes. Reset it before shrinking so collapse always
      // returns to the beginning of the body and cannot immediately re-expand.
      if (wrapperRef.current) {
        wrapperRef.current.scrollTop = 0;
      }
      onExpandedChange(false);
      return;
    }

    onExpandedChange(true);
  };

  const showsToggle =
    !isExpansionForced && (isOverflowing || showsExpandedBody);

  return (
    <div
      ref={wrapperRef}
      data-expandable-preview=""
      data-expanded={showsExpandedBody}
      style={{ height: showsExpandedBody ? "auto" : collapsedHeight }}
      className={twMerge(
        "group relative -m-2 box-content flex flex-auto flex-col overflow-hidden p-2 [interpolate-size:allow-keywords]",
        "motion-safe:supports-[interpolate-size:allow-keywords]:transition-[height] motion-safe:supports-[interpolate-size:allow-keywords]:duration-[250ms] motion-safe:supports-[interpolate-size:allow-keywords]:ease-[cubic-bezier(0.25,0.46,0.45,0.94)]",
        isOverflowing && !showsExpandedBody ? "cursor-pointer" : "",
        className,
      )}
      onClick={handleWrapperClick}
      onScroll={handleWrapperScroll}
      onTransitionEnd={handleWrapperTransitionEnd}
    >
      <div
        id={bodyId}
        ref={bodyRef}
        className="flex-auto"
        onFocusCapture={handleBodyFocusCapture}
      >
        {children}
      </div>
      {showsToggle ? (
        <>
          {/* Reserve room for the bottom-anchored control only after expansion.
              Keeping this spacer out of the collapsed state preserves the existing
              scrollHeight > clientHeight overflow determination. */}
          {showsExpandedBody ? (
            <div aria-hidden="true" className="h-6 shrink-0" />
          ) : null}
          {/* Collapsed, the whole fade is the hit area; expanded, the strip below
              the last row. `peer` lets hovering it shrink the fade toward its
              opaque bottom, revealing more without clearing the label. */}
          <button
            ref={toggleButtonRef}
            type="button"
            aria-controls={bodyId}
            aria-expanded={showsExpandedBody}
            onClick={(event) => {
              // The surrounding preview is also clickable. Keep this button's click
              // from invoking both handlers and reporting one state change twice.
              event.stopPropagation();
              handleToggleClick();
            }}
            style={showsExpandedBody ? undefined : { height: fadeHeight }}
            className={twMerge(
              "peer group/toggle absolute inset-x-0 bottom-0 z-20 flex cursor-pointer items-end justify-center pb-2 focus:outline-hidden",
              showsExpandedBody ? "h-8" : "",
            )}
          >
            <span className="rounded-xs text-xs font-medium text-gray-500 transition-colors group-hover/toggle:text-foreground group-focus-visible/toggle:text-foreground group-focus-visible/toggle:outline-2 group-focus-visible/toggle:outline-offset-2 group-focus-visible/toggle:outline-indigo-600">
              {showsExpandedBody ? collapseLabel : expandLabel}
            </span>
          </button>
          <div
            ref={gradientRef}
            aria-hidden="true"
            data-expandable-preview-gradient=""
            style={{ height: fadeHeight }}
            className={twMerge(
              "pointer-events-none absolute inset-x-0 bottom-0 z-10 origin-bottom bg-gradient-to-b from-white/0 via-white/90 to-white motion-safe:transition-[opacity,scale] motion-safe:duration-300 motion-safe:ease-out",
              showsExpandedBody
                ? "opacity-0 motion-safe:delay-[250ms]"
                : "opacity-100 peer-hover:scale-y-75 peer-focus-visible:scale-y-75",
            )}
          />
        </>
      ) : null}
    </div>
  );
};
